import {
    FormValidateOrFn as TanstackFormValidateOrFn,
    FormAsyncValidateOrFn as TanstackFormAsyncValidateOrFn
} from "@tanstack/react-form";
import { Stack, StackProps } from "@mui/material";
import { FormApi } from "../shared/types";
import { FormProvider } from "../contexts/form-context";
import { FieldSet } from "../modules/field-set";

import FormTextField from "./inputs/text-fields/text-field";
import FormNumberField from "./inputs/text-fields/number-field";
import FormEmailField from "./inputs/text-fields/email-field";
import FormPasswordField from "./inputs/text-fields/password-field";
import FormDatePicker from "./inputs/date/date-picker";
import FormSelect from "./inputs/select/select";
import FormMultiSelect from "./inputs/select/multiselect-chip";
import FormModelComboBox from "./inputs/combo-box/model-combo-box";
import FormModelComboBoxAsync from "./inputs/combo-box/model-combo-box-async";
import FormCheckBox from "./inputs/check-boxes/check-box";
import FormCheckBoxGroup from "./inputs/check-boxes/check-box-group";
import FormSubmitButton from "./inputs/buttons/submit-button";
import FormSubmitIconButton from "./inputs/buttons/submit-icon-button";
import FormIconButton from "./inputs/buttons/icon-button";
import FormButton from "./inputs/buttons/button";
import FormErrorText from "./feedback/error-text";
import FormMonedaField from "./inputs/text-fields/moneda-field";
import FormImageField from "./inputs/files/image-field";
import FormMontoField from "./inputs/text-fields/monto-field";
import FormRadioButton from "./inputs/radio/radio-button";
import FormRadioButtonGroup from "./inputs/radio/radio-button-group";

export interface FormProps<
    in out TFormData = any,
    in out TOnMount extends undefined | TanstackFormValidateOrFn<TFormData> = any,
    in out TOnChange extends undefined | TanstackFormValidateOrFn<TFormData> = any,
    in out TOnChangeAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData> = any,
    in out TOnBlur extends undefined | TanstackFormValidateOrFn<TFormData> = any,
    in out TOnBlurAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData> = any,
    in out TOnSubmit extends undefined | TanstackFormValidateOrFn<TFormData> = any,
    in out TOnSubmitAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData> = any,
    in out TOnServer extends undefined | TanstackFormAsyncValidateOrFn<TFormData> = any,
    in out TSubmitMeta = any
> extends StackProps<"div"> {
    form: FormApi<TFormData, TOnMount, TOnChange, TOnChangeAsync, TOnBlur, TOnBlurAsync, TOnSubmit, TOnSubmitAsync, TOnServer, TSubmitMeta>
}

export default function Form<
    TFormData,
    TOnMount extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnChange extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnChangeAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnBlur extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnBlurAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnSubmit extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnSubmitAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnServer extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TSubmitMeta
>({
    form,
    children,
    ...props
}: FormProps<TFormData, TOnMount, TOnChange, TOnChangeAsync, TOnBlur, TOnBlurAsync, TOnSubmit, TOnSubmitAsync, TOnServer, TSubmitMeta>) {
    return <FormProvider form={form}>
        <Stack {...props} component={FieldSet} fieldSet={form.fieldSet}>
            {children}
        </Stack>
    </FormProvider>
}

Form.TextField = FormTextField;
Form.NumberField = FormNumberField;
Form.EmailField = FormEmailField;
Form.PasswordField = FormPasswordField;
Form.MonedaField = FormMonedaField;
Form.MontoField = FormMontoField;
Form.DatePicker = FormDatePicker;
Form.Select = FormSelect;
Form.MultiSelect = FormMultiSelect;

Form.ModelComboBox = FormModelComboBox;
Form.ModelComboBoxAsync = FormModelComboBoxAsync;
Form.CheckBox = FormCheckBox;
Form.CheckBoxGroup = FormCheckBoxGroup;
Form.RadioButton = FormRadioButton;
Form.RadioGroup = FormRadioButtonGroup;
Form.ImageField = FormImageField;
Form.SubmitButton = FormSubmitButton;
Form.SubmitIconButton = FormSubmitIconButton;
Form.IconButton = FormIconButton;
Form.Button = FormButton;
Form.ErrorText = FormErrorText;
